import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const CATEGORIES = [
  { value: '腕表', labelKey: 'consignmentSubmit.catWatch' },
  { value: '珠宝', labelKey: 'consignmentSubmit.catJewelry' },
  { value: '箱包', labelKey: 'consignmentSubmit.catBag' },
  { value: '配饰', labelKey: 'consignmentSubmit.catAccessory' },
  { value: '服装', labelKey: 'consignmentSubmit.catClothing' },
];

const BRANDS_BY_CAT = {
  '腕表': ['Rolex', 'Omega', 'Cartier', 'Patek Philippe', 'Audemars Piguet', 'IWC', 'Breitling', 'TAG Heuer', 'Panerai', 'Grand Seiko', 'Tudor', 'Longines'],
  '珠宝': ['Cartier', 'Tiffany & Co.', 'Van Cleef & Arpels', 'BVLGARI', 'Harry Winston', 'Buccellati', 'Mikimoto', 'Chopard', 'Piaget'],
  '箱包': ['Hermès', 'Louis Vuitton', 'Chanel', 'Gucci', 'CELINE', 'Dior', 'Prada', 'Bottega Veneta', 'LOEWE', 'Goyard', 'Fendi'],
  '配饰': ['Hermès', 'Cartier', 'Chanel', 'Louis Vuitton', 'Gucci', 'CELINE', 'Chrome Hearts', 'Tiffany & Co.'],
  '服装': ['Chanel', 'Dior', 'Louis Vuitton', 'Gucci', 'Prada', 'Burberry', 'Max Mara', 'Moncler'],
};

const HOT_BRANDS = {
  '腕表': ['Rolex', 'Omega', 'Cartier', 'Patek Philippe', 'Audemars Piguet', 'IWC'],
  '珠宝': ['Cartier', 'Tiffany & Co.', 'Van Cleef & Arpels', 'BVLGARI', 'Harry Winston', 'Mikimoto'],
  '箱包': ['Hermès', 'Louis Vuitton', 'Chanel', 'Gucci', 'CELINE', 'Dior'],
  '配饰': ['Hermès', 'Cartier', 'Chanel', 'Chrome Hearts', 'Louis Vuitton', 'Gucci'],
  '服装': ['Chanel', 'Dior', 'Louis Vuitton', 'Gucci', 'Prada', 'Max Mara'],
};

// Short display names for brand circles
const BRAND_SHORT = {
  'Rolex': 'ROLEX',
  'Omega': 'OMEGA',
  'Cartier': 'Cartier',
  'Patek Philippe': 'PP',
  'Audemars Piguet': 'AP',
  'IWC': 'IWC',
  'Breitling': 'BRT',
  'TAG Heuer': 'TAG',
  'Panerai': 'PAM',
  'Grand Seiko': 'GS',
  'Tudor': 'TUDOR',
  'Longines': 'LG',
  'Tiffany & Co.': 'T&Co',
  'Van Cleef & Arpels': 'VCA',
  'BVLGARI': 'BVL',
  'Harry Winston': 'HW',
  'Buccellati': 'BCC',
  'Mikimoto': 'MKM',
  'Chopard': 'CHP',
  'Piaget': 'PGT',
  'Hermès': 'H',
  'Louis Vuitton': 'LV',
  'Chanel': 'CC',
  'Gucci': 'GG',
  'CELINE': 'CEL',
  'Dior': 'DIOR',
  'Prada': 'PDA',
  'Bottega Veneta': 'BV',
  'LOEWE': 'LW',
  'Goyard': 'GYD',
  'Fendi': 'FF',
  'Chrome Hearts': 'CH+',
  'Burberry': 'BBY',
  'Max Mara': 'MM',
  'Moncler': 'MCL',
};

const GRADES = [
  { key: 'S', labelKey: 'consignmentSubmit.gradeS', descKey: 'consignmentSubmit.gradeSDesc' },
  { key: 'A', labelKey: 'consignmentSubmit.gradeA', descKey: 'consignmentSubmit.gradeADesc' },
  { key: 'B', labelKey: 'consignmentSubmit.gradeB', descKey: 'consignmentSubmit.gradeBDesc' },
  { key: 'C', labelKey: 'consignmentSubmit.gradeC', descKey: 'consignmentSubmit.gradeCDesc' },
];

const STEP_LABEL_KEYS = ['consignmentSubmit.stepBrandCategory', 'consignmentSubmit.stepItemInfo', 'consignmentSubmit.stepPricing', 'consignmentSubmit.stepDelivery'];
const AZ = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function ConsignmentSubmit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const letterRefs = useRef({});

  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const fromOrder = searchParams.get('source') === 'order';

  const [form, setForm] = useState({
    category: searchParams.get('category') || '',
    brand: searchParams.get('brand') || '',
    brand_custom: '',
    item_grade: '',
    item_name: '',
    item_description: '',
    source_type: fromOrder ? 'order' : 'new',
    source_order_id: '',
    pricing_mode: 'fixed',
    price_cny: '',
    min_price_cny: '',
    storage_type: 'platform',
    delivery_method: searchParams.get('delivery') || 'pickup',
    pickup_address: '',
    tracking_no: '',
    notes: '',
  });

  const [photos, setPhotos] = useState([]);       // [{ url, uploading, file }]
  const fileInputRef = useRef(null);
  const [aiDescLoading, setAiDescLoading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleAiDescribe = async () => {
    if (aiDescLoading) return;
    setAiDescLoading(true);
    try {
      const photoUrls = photos.filter(p => !p.uploading).map(p => p.url);
      const res = await api.post('/ai/describe', {
        category: form.category,
        brand: form.brand === '__custom__' ? form.brand_custom : form.brand,
        item_name: form.item_name,
        item_grade: form.item_grade,
        photos: photoUrls,
      });
      set('item_description', res.data.description);
      message.success(t('consignmentSubmit.aiDescGenerated'));
    } catch (err) {
      const msg = err.response?.data?.error || t('consignmentSubmit.aiUnavailable');
      message.error(msg);
    } finally { setAiDescLoading(false); }
  };

  const handlePhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (e.target) e.target.value = '';

    const remaining = 9 - photos.length;
    if (remaining <= 0) { message.warning(t('consignmentSubmit.maxPhotos')); return; }
    const toUpload = files.slice(0, remaining).filter(f => {
      if (f.size > 10 * 1024 * 1024) { message.warning(t('consignmentSubmit.fileTooLarge', { name: f.name })); return false; }
      return true;
    });
    if (!toUpload.length) return;

    // Add all previews at once, then upload in parallel
    const placeholders = toUpload.map(file => ({
      url: URL.createObjectURL(file),
      uploading: true,
      _file: file,
      _id: Math.random().toString(36).slice(2),
    }));
    setPhotos(prev => [...prev, ...placeholders]);

    // Upload each file
    await Promise.all(placeholders.map(async (ph) => {
      const fd = new FormData();
      fd.append('file', ph._file);
      try {
        const res = await api.post('/upload?folder=consignment', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPhotos(prev => prev.map(p => p._id === ph._id ? { url: res.data.url, uploading: false, _id: ph._id } : p));
      } catch {
        message.error(t('consignmentSubmit.uploadFailed'));
        setPhotos(prev => prev.filter(p => p._id !== ph._id));
      }
    }));
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (fromOrder) {
      api.get('/orders', { params: { status: 'delivered', limit: 50 } })
        .then(r => setDeliveredOrders(r.data?.data || r.data || []))
        .catch(() => {});
    }
  }, [fromOrder]);

  const brands = BRANDS_BY_CAT[form.category] || [];
  const hotBrands = HOT_BRANDS[form.category] || brands.slice(0, 6);

  // Build A-Z grouped brands
  const allBrandsSorted = [...brands].sort((a, b) => a.localeCompare(b, 'en'));
  const brandsByLetter = {};
  allBrandsSorted.forEach(b => {
    const letter = b.charAt(0).toUpperCase();
    if (!brandsByLetter[letter]) brandsByLetter[letter] = [];
    brandsByLetter[letter].push(b);
  });
  const availableLetters = AZ.filter(l => brandsByLetter[l]?.length > 0);

  const scrollToLetter = (letter) => {
    const el = letterRefs.current[letter];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filteredBrands = brandSearch
    ? allBrandsSorted.filter(b => b.toLowerCase().includes(brandSearch.toLowerCase()))
    : null;

  const handleSubmit = async () => {
    if (!form.item_name) { message.warning(t('consignmentSubmit.itemNameRequired')); return; }
    if (!form.price_cny || Number(form.price_cny) <= 0) { message.warning(t('consignment.validPriceRequired')); return; }
    if (photos.some(p => p.uploading)) { message.warning(t('consignmentSubmit.photosUploading')); return; }

    setSubmitting(true);
    try {
      const photoUrls = photos.filter(p => !p.uploading).map(p => p.url);
      const payload = {
        item_name: form.item_name,
        item_description: form.item_description,
        item_grade: form.item_grade,
        pricing_mode: form.pricing_mode,
        price_cny: Number(form.price_cny),
        min_price_cny: form.min_price_cny ? Number(form.min_price_cny) : null,
        storage_type: form.storage_type,
        source_type: form.source_type,
        source_order_id: form.source_order_id || null,
        brand: form.brand === '__custom__' ? form.brand_custom : form.brand,
        category: form.category,
        notes: form.notes,
        photos: photoUrls.length > 0 ? photoUrls : null,
      };
      const res = await api.post('/consignments', payload);
      message.success(t('consignmentSubmit.submitSuccess', { itemNo: res.data.item_no }));
      navigate('/consignment');
    } catch (e) {
      message.error(e.response?.data?.error || t('common.failed'));
    } finally { setSubmitting(false); }
  };

  const px = isMobile ? 16 : 48;

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: `1px solid ${BD}`, borderRadius: 6,
    fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff',
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: INK, marginBottom: 6 };

  const canNext = () => {
    if (step === 0) return form.category && form.brand;
    if (step === 1) return form.item_name;
    if (step === 2) return form.price_cny && Number(form.price_cny) > 0;
    return true;
  };

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${BD}`, padding: `16px ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <button onClick={() => step > 0 ? setStep(step - 1) : navigate('/consignment')} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: 13, marginBottom: 12,
          }}>{step > 0 ? `← ${t('consignmentSubmit.prevStep')}` : `← ${t('common.back')}`}</button>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 700, color: INK, marginBottom: 14 }}>{t('consignment.startConsignment')}</div>

          {/* Step bar */}
          <div style={{ display: 'flex', gap: 4 }}>
            {STEP_LABEL_KEYS.map((s, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: 3, borderRadius: 2, marginBottom: 6,
                  background: i <= step ? GOLD : BD,
                  transition: 'background .3s',
                }} />
                <span style={{ fontSize: 11, color: i <= step ? GOLD : MUTED, fontWeight: i === step ? 600 : 400 }}>{t(s)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: `24px ${px}px` }}>

        {/* STEP 0: 品牌品类 */}
        {step === 0 && (
          <div>
            {/* Source type */}
            {fromOrder && (
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>{t('consignmentSubmit.sourceOrder')}</label>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('consignmentSubmit.selectDeliveredOrder')}
                  value={form.source_order_id || undefined}
                  onChange={v => {
                    set('source_order_id', v);
                    const order = deliveredOrders.find(o => o.id === v);
                    if (order) {
                      set('item_name', order.lot_name || order.item_name || '');
                    }
                  }}
                >
                  {deliveredOrders.map(o => (
                    <Select.Option key={o.id} value={o.id}>
                      {o.order_no} — {o.lot_name || o.item_name || '—'}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            <label style={labelStyle}>{t('consignment.category')} *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
              {CATEGORIES.map(c => (
                <button key={c.value} onClick={() => { set('category', c.value); set('brand', ''); setShowAllBrands(false); setBrandSearch(''); }} style={{
                  padding: '12px 0', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500,
                  border: `1px solid ${form.category === c.value ? GOLD : BD}`,
                  background: form.category === c.value ? 'rgba(184,137,42,.08)' : '#fff',
                  color: form.category === c.value ? GOLD : INK,
                }}>{t(c.labelKey)}</button>
              ))}
            </div>

            {form.category && !showAllBrands && (
              <>
                {/* Hot brands grid */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>{t('consignmentSubmit.hotBrands')} *</label>
                  <button onClick={() => setShowAllBrands(true)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: GOLD, fontSize: 12,
                  }}>{t('consignmentSubmit.allBrands')} ›</button>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
                  gap: 10, marginBottom: 16,
                }}>
                  {hotBrands.map(b => (
                    <button key={b} onClick={() => set('brand', b)} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      padding: '14px 6px', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${form.brand === b ? GOLD : BD}`,
                      background: form.brand === b ? 'rgba(184,137,42,.08)' : '#fff',
                      transition: 'all .15s',
                    }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', background: form.brand === b ? GOLD : '#F5EDD8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: BRAND_SHORT[b]?.length > 3 ? 10 : 12,
                        fontWeight: 700, color: form.brand === b ? '#fff' : INK,
                        fontFamily: "'Georgia', serif", letterSpacing: 0.5,
                        transition: 'all .15s',
                      }}>
                        {BRAND_SHORT[b] || b.slice(0, 3).toUpperCase()}
                      </div>
                      <div style={{
                        fontSize: 11, color: form.brand === b ? GOLD : MUTED,
                        textAlign: 'center', lineHeight: 1.2, fontWeight: form.brand === b ? 600 : 400,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                      }}>{b}</div>
                    </button>
                  ))}
                </div>

                {/* Other brands in list */}
                {brands.filter(b => !hotBrands.includes(b)).length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>{t('consignmentSubmit.moreBrands')}</div>
                    <div style={{
                      display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                      gap: 8,
                    }}>
                      {brands.filter(b => !hotBrands.includes(b)).map(b => (
                        <button key={b} onClick={() => set('brand', b)} style={{
                          padding: '10px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                          fontFamily: "'Georgia', serif", letterSpacing: 0.5,
                          border: `1px solid ${form.brand === b ? GOLD : BD}`,
                          background: form.brand === b ? 'rgba(184,137,42,.08)' : '#fff',
                          color: form.brand === b ? GOLD : INK, textAlign: 'left',
                        }}>{b}</button>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={() => set('brand', '__custom__')} style={{
                  padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  border: `1px solid ${form.brand === '__custom__' ? GOLD : BD}`,
                  background: form.brand === '__custom__' ? 'rgba(184,137,42,.08)' : '#fff',
                  color: form.brand === '__custom__' ? GOLD : MUTED, width: '100%', textAlign: 'left',
                }}>{t('consignmentSubmit.otherBrand')}</button>
                {form.brand === '__custom__' && (
                  <input value={form.brand_custom} onChange={e => set('brand_custom', e.target.value)}
                    placeholder={t('consignmentSubmit.brandNamePlaceholder')} style={{ ...inputStyle, marginTop: 8 }} />
                )}
              </>
            )}

            {/* A-Z Full brand list view */}
            {form.category && showAllBrands && (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>{t('consignmentSubmit.allBrands')} *</label>
                  <button onClick={() => setShowAllBrands(false)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: GOLD, fontSize: 12,
                  }}>← {t('consignmentSubmit.backToHot')}</button>
                </div>

                {/* Search */}
                <input
                  value={brandSearch}
                  onChange={e => setBrandSearch(e.target.value)}
                  placeholder={t('consignmentSubmit.searchBrand')}
                  style={{ ...inputStyle, marginBottom: 12 }}
                />

                <div style={{ display: 'flex', gap: 8 }}>
                  {/* Brand list */}
                  <div style={{ flex: 1 }}>
                    {filteredBrands ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {filteredBrands.map(b => (
                          <button key={b} onClick={() => { set('brand', b); setShowAllBrands(false); setBrandSearch(''); }} style={{
                            padding: '10px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 14,
                            fontFamily: "'Georgia', serif", textAlign: 'left',
                            border: `1px solid ${form.brand === b ? GOLD : BD}`,
                            background: form.brand === b ? 'rgba(184,137,42,.08)' : '#fff',
                            color: form.brand === b ? GOLD : INK,
                          }}>{b}</button>
                        ))}
                        {filteredBrands.length === 0 && (
                          <div style={{ padding: 20, textAlign: 'center', color: MUTED, fontSize: 13 }}>
                            {t('consignmentSubmit.noBrandMatch')}
                          </div>
                        )}
                      </div>
                    ) : (
                      availableLetters.map(letter => (
                        <div key={letter} ref={el => letterRefs.current[letter] = el}>
                          <div style={{
                            fontSize: 14, fontWeight: 700, color: GOLD, padding: '10px 0 6px',
                            borderBottom: `1px solid ${BD}40`, marginBottom: 6,
                          }}>{letter}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                            {brandsByLetter[letter].map(b => (
                              <button key={b} onClick={() => { set('brand', b); setShowAllBrands(false); setBrandSearch(''); }} style={{
                                padding: '10px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 14,
                                fontFamily: "'Georgia', serif", textAlign: 'left',
                                border: `1px solid ${form.brand === b ? GOLD : BD}`,
                                background: form.brand === b ? 'rgba(184,137,42,.08)' : '#fff',
                                color: form.brand === b ? GOLD : INK,
                              }}>{b}</button>
                            ))}
                          </div>
                        </div>
                      ))
                    )}

                    <button onClick={() => { set('brand', '__custom__'); setShowAllBrands(false); setBrandSearch(''); }} style={{
                      padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                      border: `1px solid ${BD}`, background: '#fff', color: MUTED, width: '100%',
                      textAlign: 'left', marginTop: 8,
                    }}>{t('consignmentSubmit.otherBrand')}</button>
                  </div>

                  {/* Side letter index */}
                  {!brandSearch && (
                    <div style={{
                      position: 'sticky', top: 80, alignSelf: 'flex-start',
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      padding: '4px 2px', background: 'rgba(255,255,255,.9)', borderRadius: 12,
                      boxShadow: '0 1px 4px rgba(0,0,0,.08)',
                    }}>
                      {AZ.map(l => {
                        const active = availableLetters.includes(l);
                        return (
                          <button key={l} onClick={() => active && scrollToLetter(l)} style={{
                            width: 22, height: 18, border: 'none', background: 'transparent',
                            cursor: active ? 'pointer' : 'default',
                            fontSize: 10, fontWeight: 600,
                            color: active ? INK : '#D0C8B8',
                            padding: 0,
                          }}>{l}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Grade */}
            {!showAllBrands && (
              <>
                <label style={{ ...labelStyle, marginTop: 20 }}>{t('consignmentSubmit.selfGrade')} *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {GRADES.map(g => (
                    <button key={g.key} onClick={() => set('item_grade', g.key)} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 6,
                      border: `1px solid ${form.item_grade === g.key ? GOLD : BD}`,
                      background: form.item_grade === g.key ? 'rgba(184,137,42,.08)' : '#fff',
                      cursor: 'pointer', textAlign: 'left',
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        border: `2px solid ${form.item_grade === g.key ? GOLD : BD}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {form.item_grade === g.key && <div style={{ width: 10, height: 10, borderRadius: '50%', background: GOLD }} />}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: INK }}>{t(g.labelKey)}</div>
                        <div style={{ fontSize: 11, color: MUTED }}>{t(g.descKey)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 1: 商品信息 */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{t('consignmentSubmit.itemNameLabel')} *</label>
              <input value={form.item_name} onChange={e => set('item_name', e.target.value)}
                placeholder={`${form.brand !== '__custom__' ? form.brand + ' ' : ''}${t('consignmentSubmit.itemNamePlaceholder')}`}
                style={inputStyle} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>{t('consignment.description')}</label>
                <button onClick={handleAiDescribe} disabled={aiDescLoading || (!form.item_name && !form.brand)}
                  style={{
                    padding: '4px 12px', borderRadius: 14, fontSize: 11, fontWeight: 500,
                    border: `1px solid ${GOLD}`, cursor: aiDescLoading ? 'wait' : 'pointer',
                    background: 'rgba(184,137,42,.08)', color: GOLD,
                    opacity: (!form.item_name && !form.brand) ? 0.4 : 1,
                  }}>
                  {aiDescLoading ? t('consignmentSubmit.aiGenerating') : t('consignmentSubmit.aiAutoGenerate')}
                </button>
              </div>
              <textarea value={form.item_description} onChange={e => set('item_description', e.target.value)}
                placeholder={t('consignmentSubmit.descPlaceholder')}
                rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{t('consignment.photos')}</label>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple
                capture={undefined} style={{ display: 'none' }} onChange={handlePhotoPick} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {photos.map((p, i) => (
                  <div key={i} style={{
                    width: 80, height: 80, borderRadius: 6, overflow: 'hidden',
                    position: 'relative', border: `1px solid ${BD}`, flexShrink: 0,
                  }}>
                    <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover',
                      opacity: p.uploading ? 0.4 : 1 }} />
                    {p.uploading && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 11, color: MUTED }}>
                        {t('consignmentSubmit.uploading')}
                      </div>
                    )}
                    {!p.uploading && (
                      <button onClick={() => removePhoto(i)} style={{
                        position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%',
                        background: 'rgba(0,0,0,.5)', color: '#fff', border: 'none', cursor: 'pointer',
                        fontSize: 11, lineHeight: '18px', padding: 0,
                      }}>×</button>
                    )}
                    {i === 0 && !p.uploading && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'rgba(0,0,0,.5)', color: '#fff', fontSize: 9, textAlign: 'center',
                        padding: '1px 0' }}>{t('consignmentSubmit.cover')}</div>
                    )}
                  </div>
                ))}
                {photos.length < 9 && (
                  <div onClick={() => fileInputRef.current?.click()} style={{
                    width: 80, height: 80, borderRadius: 6, border: `2px dashed ${BD}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', background: '#fff', flexShrink: 0,
                  }}>
                    <span style={{ fontSize: 22, color: MUTED }}>+</span>
                    <span style={{ fontSize: 10, color: MUTED }}>{photos.length}/9</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#A89070', marginTop: 6 }}>{t('consignmentSubmit.photoTips')}</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{t('consignmentSubmit.notes')}</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder={t('consignmentSubmit.notesPlaceholder')} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
        )}

        {/* STEP 2: 定价 */}
        {step === 2 && (
          <div>
            <label style={labelStyle}>{t('consignment.pricingMode')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
              {[
                { key: 'fixed', label: t('market.fixedPrice'), desc: t('consignmentSubmit.fixedPriceDesc') },
                { key: 'offer', label: t('market.negotiable'), desc: t('consignmentSubmit.offerDesc') },
                { key: 'auction', label: t('market.auctionMode'), desc: t('consignmentSubmit.auctionDesc') },
              ].map(m => (
                <button key={m.key} onClick={() => set('pricing_mode', m.key)} style={{
                  padding: '14px 10px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                  border: `1px solid ${form.pricing_mode === m.key ? GOLD : BD}`,
                  background: form.pricing_mode === m.key ? 'rgba(184,137,42,.08)' : '#fff',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: form.pricing_mode === m.key ? GOLD : INK }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{m.desc}</div>
                </button>
              ))}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{form.pricing_mode === 'offer' ? t('consignmentSubmit.listPrice') : t('consignment.sellingPrice')} (CNY) *</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: GOLD, fontWeight: 600, fontSize: 16 }}>¥</span>
                <input type="number" value={form.price_cny} onChange={e => set('price_cny', e.target.value)}
                  placeholder="0" style={{ ...inputStyle, paddingLeft: 32, fontSize: 18, fontFamily: 'var(--mono)' }} />
              </div>
            </div>

            {form.pricing_mode === 'offer' && (
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>{t('consignment.minAcceptPrice')} (CNY)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: MUTED }}>¥</span>
                  <input type="number" value={form.min_price_cny} onChange={e => set('min_price_cny', e.target.value)}
                    placeholder={t('consignment.minPricePlaceholder')} style={{ ...inputStyle, paddingLeft: 32 }} />
                </div>
              </div>
            )}

            {/* Revenue estimate */}
            {form.price_cny && Number(form.price_cny) > 0 && (
              <div style={{
                padding: 16, background: '#fff', border: `1px solid ${BD}`, borderRadius: 8,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: INK, marginBottom: 10 }}>{t('consignmentSubmit.revenueEstimate')}</div>
                {[
                  [t('consignment.sellingPrice'), `¥${Number(form.price_cny).toLocaleString()}`],
                  [t('consignmentSubmit.platformCommission'), `-¥${Math.round(Number(form.price_cny) * 0.08).toLocaleString()}`],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: MUTED }}><span>{k}</span><span>{v}</span></div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginTop: 4, borderTop: `1px solid ${BD}`, fontSize: 15, fontWeight: 600 }}>
                  <span style={{ color: INK }}>{t('consignmentSubmit.estimatedNet')}</span>
                  <span style={{ color: GOLD, fontFamily: 'var(--mono)' }}>
                    ¥{Math.round(Number(form.price_cny) * 0.92).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: 交付方式 */}
        {step === 3 && (
          <div>
            <label style={labelStyle}>{t('consignmentSubmit.deliveryMethod')}</label>
            <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${BD}`, marginBottom: 20 }}>
              {[
                { key: 'pickup', label: t('consignmentSubmit.pickup'), sub: t('consignmentSubmit.pickupFree') },
                { key: 'self',   label: t('consignmentSubmit.selfShip'), sub: t('consignmentSubmit.selfPay') },
                { key: 'store',  label: t('consignmentSubmit.storeDelivery'), sub: t('consignmentSubmit.faceInspection') },
              ].map(d => (
                <button key={d.key} onClick={() => set('delivery_method', d.key)} style={{
                  flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer', textAlign: 'center',
                  background: 'transparent',
                  borderBottom: form.delivery_method === d.key ? `2px solid ${GOLD}` : '2px solid transparent',
                }}>
                  <div style={{ fontSize: 14, fontWeight: form.delivery_method === d.key ? 600 : 400, color: form.delivery_method === d.key ? INK : MUTED }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>{d.sub}</div>
                </button>
              ))}
            </div>

            {form.delivery_method === 'pickup' && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>{t('consignmentSubmit.pickupAddress')}</label>
                  <textarea value={form.pickup_address} onChange={e => set('pickup_address', e.target.value)}
                    placeholder={t('consignmentSubmit.pickupAddressPlaceholder')} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div style={{ padding: 14, background: '#F5EDD8', borderRadius: 8, fontSize: 12, color: MUTED, lineHeight: 1.7 }}>
                  {t('consignmentSubmit.pickupNote')}
                </div>
              </div>
            )}

            {form.delivery_method === 'self' && (
              <div>
                <div style={{
                  padding: 16, background: '#fff', border: `1px solid ${BD}`, borderRadius: 8, marginBottom: 16,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: INK }}>{t('consignmentSubmit.shippingAddress')}</span>
                    <button onClick={() => {
                      navigator.clipboard?.writeText('東京都港区赤坂2-17-22 云川中古 収');
                      message.success(t('consignmentSubmit.addressCopied'));
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GOLD, fontSize: 12 }}>
                      {t('common.copy')}
                    </button>
                  </div>
                  <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.7 }}>
                    <div>東京都港区赤坂2-17-22</div>
                    <div style={{ fontWeight: 600, color: INK }}>云川中古 収</div>
                    <div>TEL: 13942628951</div>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>{t('consignmentSubmit.trackingNo')}</label>
                  <input value={form.tracking_no} onChange={e => set('tracking_no', e.target.value)}
                    placeholder={t('consignmentSubmit.trackingNoPlaceholder')} style={inputStyle} />
                </div>
              </div>
            )}

            {form.delivery_method === 'store' && (
              <div style={{ padding: 16, background: '#fff', border: `1px solid ${BD}`, borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 10 }}>{t('consignmentSubmit.storeAddress')}</div>
                {[
                  { city: t('consignmentSubmit.cityTokyo'), addr: '東京都港区赤坂2-17-22', tel: '13942628951' },
                  { city: t('consignmentSubmit.cityOsaka'), addr: '大阪市中央区心斎橋筋1-XX', tel: '13942628951' },
                  { city: t('consignmentSubmit.cityHongKong'), addr: '香港中环皇后大道中XX号', tel: '13942628951' },
                ].map(s => (
                  <div key={s.city} style={{ padding: '10px 0', borderBottom: `1px solid ${BD}` }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{s.city}</div>
                    <div style={{ fontSize: 12, color: MUTED }}>{s.addr} · {s.tel}</div>
                  </div>
                ))}
                <div style={{ marginTop: 10, fontSize: 12, color: MUTED }}>
                  {t('consignmentSubmit.storeNote')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* NAV BUTTONS */}
        {!(step === 0 && showAllBrands) && (
          <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} style={{
                flex: '0 0 100px', padding: '14px 0', borderRadius: 6,
                border: `1px solid ${BD}`, background: '#fff', color: MUTED, fontSize: 14, cursor: 'pointer',
              }}>{t('consignmentSubmit.prevStep')}</button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canNext()} style={{
                flex: 1, padding: '14px 0', borderRadius: 6, border: 'none',
                background: canNext() ? GOLD : BD, color: canNext() ? '#fff' : MUTED,
                fontSize: 15, fontWeight: 600, cursor: canNext() ? 'pointer' : 'not-allowed',
              }}>{t('consignmentSubmit.nextStep')}</button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting} style={{
                flex: 1, padding: '14px 0', borderRadius: 6, border: 'none',
                background: GOLD, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                opacity: submitting ? 0.6 : 1,
                boxShadow: '0 4px 16px rgba(184,137,42,.35)',
              }}>{submitting ? t('consignmentSubmit.submitting') : t('consignmentSubmit.submitConsignment')}</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
